import { LEGAL_KINDS, PortalError, type LegalKind, type PortalApi, type PortalContact, type PortalLayout, type PortalLogoSize, type PortalTheme } from "./api";
import { cleanCopy } from "./copy";
import { modeForGround, parseHex } from "./palette";

/**
 * The console's full-page theme preview: the REAL portal page, in a frame,
 * painted with a theme the operator has not necessarily published.
 *
 * The console frames /portal.html?preview=1, the portal says it is ready, and
 * the console posts the theme. So the operator sees exactly what a guest's
 * browser renders -- same bundle, same portal.css, same palette -- for a
 * variation, a saved draft or the live theme, without publishing anything.
 *
 * 🔑 ONLY THE SAME ORIGIN, ONLY FROM THE PARENT, ONLY WHEN FRAMED. A preview
 * message changes colours and a logo on one browser tab and nothing else, but
 * a guest page must still never be re-skinnable by another site: nginx lets
 * only the same origin frame /portal (frame-ancestors 'self'), and a message
 * is honoured only from our own parent window on our own origin.
 *
 * No JSX and no console imports: this file is part of the portal bundle.
 */

export const PREVIEW_THEME = "ankhos-portal-preview:theme";
export const PREVIEW_READY = "ankhos-portal-preview:ready";

/** The screens the preview can show. Inner screens run on the mock API. */
export const PREVIEW_SCREENS = ["login", "register", "forgot", "myplan", "recharge", "payment", "connected", "already", "loggedout", "loggingin", "legal"] as const;
export type PreviewScreen = (typeof PREVIEW_SCREENS)[number];
export const isPreviewScreen = (s: unknown): s is PreviewScreen => (PREVIEW_SCREENS as readonly unknown[]).includes(s);

/** The operator's saved documents by kind, as radaccess returns them; "pricing" is the note under the plans. */
export type PreviewDocuments = Partial<Record<LegalKind, string>>;

/** What the console posts. Everything is re-validated here; nothing is trusted because it came from us. */
export interface PreviewMessage {
  type: typeof PREVIEW_THEME;
  title: string;
  subtitle: string;
  logoUrl: string | null;
  ground: string;
  ink: string;
  accent: string;
  /** Page text by catalogue key; each entry is checked as the portal checks a published theme. */
  copy?: Record<string, string>;
  screen?: PreviewScreen;
  /** The page colour; absent or invalid is the ground colour. */
  backdrop?: string;
  /** The background image: an object URL on this origin, or a PNG/JPEG data URI. */
  backgroundUrl?: string | null;
  layout?: PortalLayout;
  cardOpacity?: number;
  logoSize?: PortalLogoSize;
  /** The operator's public contact, as the footer shows it. */
  contact?: PortalContact;
  /** The operator's saved documents. Absent: the console could not read them, and the preview shows sample text. */
  documents?: PreviewDocuments;
}

/** The screen a preview message asks for; the sign-in page for anything else. */
export function previewScreenOf(data: unknown): PreviewScreen {
  const s = (data as { screen?: unknown } | null)?.screen;
  return isPreviewScreen(s) ? s : "login";
}

/** True only for ?preview=1 in a framed page. A top-level visit renders the normal portal. */
export function isPreview(search: string, framed: boolean): boolean {
  return framed && new URLSearchParams(search).get("preview") === "1";
}

const IMAGE = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/;

function safeImage(url: unknown, origin: string): string | null {
  if (typeof url !== "string") return null;
  // An object URL the console made from the image bytes, or the image inline.
  if (url.startsWith(`blob:${origin}/`) || IMAGE.test(url)) return url;
  return null;
}

const text = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");

function safeContact(v: unknown): PortalContact | null {
  if (!v || typeof v !== "object" || Array.isArray(v)) return null;
  const c = v as Record<string, unknown>;
  const out: PortalContact = {};
  for (const [field, max] of [["address", 500], ["city", 100], ["state", 100], ["pin", 20], ["country", 100], ["phone", 24], ["email", 255]] as const) {
    const s = text(c[field], max).trim();
    if (s) out[field] = s;
  }
  // The footer re-checks the website (ui.tsx safeWebsite); length is bounded here.
  const w = text(c.website, 2048).trim();
  if (w) out.website = w;
  return Object.keys(out).length ? out : null;
}

/** The most a document may hold; radaccess refuses more. */
const MAX_DOCUMENT_CHARS = 20000;

/**
 * The documents a preview message carries, or null when it carries none. A
 * document that is not text, is blank or is longer than radaccess allows is
 * dropped on its own. The body stays plain text: Legal renders text nodes only.
 */
export function previewDocuments(data: unknown): PreviewDocuments | null {
  const d = (data as { documents?: unknown } | null)?.documents;
  if (!d || typeof d !== "object" || Array.isArray(d)) return null;
  const out: PreviewDocuments = {};
  for (const kind of LEGAL_KINDS) {
    const body = (d as Record<string, unknown>)[kind];
    if (typeof body === "string" && body.trim() && [...body].length <= MAX_DOCUMENT_CHARS) out[kind] = body;
  }
  return out;
}

/**
 * The preview's API with the operator's own documents in place of the sample
 * text: a document it has not written is not found, as on the portal, and its
 * pricing note sits under the sample plans.
 */
export function withDocuments(api: PortalApi, documents: PreviewDocuments): PortalApi {
  return {
    ...api,
    legalDocument: (_t, kind) => {
      const body = documents[kind];
      return body === undefined ? Promise.reject(new PortalError({ kind: "not_found" })) : Promise.resolve({ kind, body });
    },
    pricing: async (t) => {
      const { plans } = await api.pricing(t);
      return documents.pricing === undefined ? { plans } : { plans, note: documents.pricing };
    },
  };
}

/**
 * The PortalTheme for a message, or null when it is not a preview message this
 * page may honour: wrong origin, not from the parent, wrong shape, or a colour
 * that is not exactly six hex digits. The optional page fields are dropped one
 * by one when invalid; they never cost the preview its colours.
 */
export function previewTheme(
  e: { origin: string; source: unknown; data: unknown },
  self: { origin: string; parent: unknown },
): PortalTheme | null {
  if (e.origin !== self.origin || e.source !== self.parent) return null;
  const d = e.data as Partial<PreviewMessage> | null;
  if (!d || typeof d !== "object" || d.type !== PREVIEW_THEME) return null;
  const { ground, ink, accent } = d;
  if (typeof ground !== "string" || typeof ink !== "string" || typeof accent !== "string") return null;
  if (!parseHex(ground) || !parseHex(ink) || !parseHex(accent)) return null;
  const copy = cleanCopy(d.copy);
  const background = safeImage(d.backgroundUrl, self.origin);
  const contact = safeContact(d.contact);
  const documents = previewDocuments(d);
  return {
    ...(Object.keys(copy).length ? { copy } : {}),
    title: text(d.title, 120) || "Wi-Fi",
    subtitle: text(d.subtitle, 240),
    logoUrl: safeImage(d.logoUrl, self.origin),
    accent,
    ground,
    ink,
    mode: modeForGround(ground),
    published: true,
    ...(typeof d.backdrop === "string" && parseHex(d.backdrop) ? { backdrop: d.backdrop } : {}),
    ...(d.layout === "compact" || d.layout === "wide" || d.layout === "full" ? { layout: d.layout } : {}),
    ...(Number.isInteger(d.cardOpacity) && d.cardOpacity! >= 80 && d.cardOpacity! <= 100 ? { cardOpacity: d.cardOpacity! } : {}),
    ...(d.logoSize === "small" || d.logoSize === "medium" || d.logoSize === "large" || d.logoSize === "xlarge" ? { logoSize: d.logoSize } : {}),
    ...(background ? { backgroundUrl: background } : {}),
    ...(contact ? { contact } : {}),
    // With the operator's documents, the pages the portal lists: the ones it
    // has written, and pricing, whose plans here are samples. Without them,
    // every page, on sample text, so the page can still be judged.
    legal: documents ? LEGAL_KINDS.filter((k) => k === "pricing" || documents[k] !== undefined) : [...LEGAL_KINDS],
    registerHeading: "",
    registerLead: "",
    planLabels: "",
    planAnnotations: "",
  };
}
